import { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { Input } from './ui/input';

interface AddressSearchProps {
 value: string;
 onChange: (address: string) => void;
 onLocationSelect: (lat: number, lng: number, address: string) => void;
 disabled?: boolean;
}

interface NominatimResult {
 place_id: number;
 display_name: string;
 lat: string;
 lon: string;
}

export function AddressSearch({ value, onChange, onLocationSelect, disabled }: AddressSearchProps) {
 const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
 const [isSearching, setIsSearching] = useState(false);
 const [showResults, setShowResults] = useState(false);
 const [isFocused, setIsFocused] = useState(false);
 const hasUserInteracted = useRef(false);

 // Fetch results when user types
 useEffect(() => {
 if (!value || value.length < 3) {
 setSearchResults([]);
 setShowResults(false);
 return;
 }

 const timeoutId = setTimeout(async () => {
 setIsSearching(true);
 try {
 const response = await fetch(
 `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=5&addressdetails=1&email=contact@discgo.app`
 );

 if (!response.ok) {
 throw new Error(`Geocoding failed: ${response.status}`);
 }

 const data = await response.json();
 setSearchResults(data);
 // Show results if user has interacted and is still focused
 if (hasUserInteracted.current && isFocused && data.length > 0) {
 setShowResults(true);
 }
 } catch (error) {
 console.error('Geocoding error:', error);
 setSearchResults([]);
 setShowResults(false);
 } finally {
 setIsSearching(false);
 }
 }, 500);

 return () => clearTimeout(timeoutId);
 }, [value, isFocused]);

 const handleSelectResult = (result: NominatimResult) => {
 const lat = parseFloat(result.lat);
 const lng = parseFloat(result.lon);
 onChange(result.display_name);
 onLocationSelect(lat, lng, result.display_name);
 setShowResults(false);
 setSearchResults([]);
 };

 return (
 <div className="relative">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
 <Input
 type="text"
 value={value}
 onChange={(e) => {
 hasUserInteracted.current = true;
 onChange(e.target.value);
 }}
 onFocus={() => {
 setIsFocused(true);
 // Show cached results if user has interacted before
 if (hasUserInteracted.current && searchResults.length > 0) {
 setShowResults(true);
 }
 }}
 onBlur={() => {
 setIsFocused(false);
 // Delay hiding to allow clicking on results
 setTimeout(() => setShowResults(false), 200);
 }}
 placeholder="Search for an address or location..."
 style={{ paddingLeft: '2rem' }}
 disabled={disabled}
 />
 </div>

 {showResults && searchResults.length > 0 && (
 <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
 {searchResults.map((result) => (
 <button
 key={result.place_id}
 onClick={() => handleSelectResult(result)}
 className="w-full px-4 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
 >
 <div className="text-sm font-medium text-gray-900">
 {result.display_name}
 </div>
 </button>
 ))}
 </div>
 )}

 {isSearching && (
 <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
 <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
 </div>
 )}
 </div>
 );
}
